/**
 * Lists task comments assigned to the current user with due dates and completion toggles.
 */
import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tasksApi, type MyTaskItem, type MyTasksResponse } from "lib/api/tasks";
import { useUser } from "util/UserContext";
import { useAuthGate } from "hooks/useAuthGate";
import RequireAuth from "components/Auth/RequireAuth";
import { getTaskUiStrings, resolveTaskLocale } from "i18n/taskUi";
import { Loader2 } from "lucide-react";

function getCommentId(comment: Record<string, unknown>): number | null {
  const raw = comment.id;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === "string") {
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function getActionPostId(comment: Record<string, unknown>): number | null {
  const raw = comment.commentable_id;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === "string") {
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function getTaskBody(comment: Record<string, unknown>): string {
  const c = comment.content;
  return typeof c === "string" ? c : "";
}

const UserTasksPage: React.FC = () => {
  const { currentUser } = useUser();
  const { requireAuth } = useAuthGate();
  const queryClient = useQueryClient();
  const labels = useMemo(() => getTaskUiStrings(), []);
  const locale = useMemo(() => resolveTaskLocale(), []);
  const tasksQueryKey = useMemo(() => ["tasks", "mine", currentUser?.id] as const, [currentUser?.id]);

  const listQuery = useQuery({
    queryKey: tasksQueryKey,
    queryFn: () => tasksApi.listMine({ limit: 50, offset: 0 }),
    enabled: Boolean(currentUser?.id),
  });

  const completionMutation = useMutation({
    mutationFn: ({ commentId, complete }: { commentId: number; complete: boolean }) =>
      tasksApi.setCompletion(commentId, complete),
    onMutate: async ({ commentId, complete }) => {
      await queryClient.cancelQueries({ queryKey: tasksQueryKey });
      const previousTasks = queryClient.getQueryData<MyTasksResponse>(tasksQueryKey);

      queryClient.setQueryData<MyTasksResponse>(tasksQueryKey, (currentTasks) => {
        if (!currentTasks) {
          return currentTasks;
        }

        return {
          ...currentTasks,
          items: currentTasks.items.map((item) => {
            const currentCommentId = getCommentId(item.comment);
            if (currentCommentId !== commentId) {
              return item;
            }
            return {
              ...item,
              completed: complete,
            };
          }),
        };
      });

      return { previousTasks };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(tasksQueryKey, context.previousTasks);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: tasksQueryKey });
    },
  });

  const onToggleComplete = useCallback(
    (item: MyTaskItem) => {
      if (!requireAuth()) {
        return;
      }
      const id = getCommentId(item.comment);
      if (id == null) {
        return;
      }
      void completionMutation.mutateAsync({ commentId: id, complete: !item.completed });
    },
    [completionMutation, requireAuth]
  );

  // When the viewer is not authenticated we delegate to the shared auth gate
  // so the URL is preserved and a clear sign-in CTA is offered (matches the
  // behavior used on settings, chat, and any other auth-only surface).
    if (!currentUser?.id) {
    return <RequireAuth>{null}</RequireAuth>;
  }

  if (listQuery.isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10 flex items-center gap-2 text-eoe-text-sub dark:text-eoe-dark-text-sub">
        <Loader2 className="size-5 animate-spin" aria-hidden />
        <span>{labels.loading}</span>
      </div>
    );
  }

  if (listQuery.isError) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-semibold mb-2">{labels.pageTitle}</h1>
        <p className="text-red-600 dark:text-red-400">{labels.error}</p>
      </div>
    );
  }

  const items = listQuery.data?.items ?? [];

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <h1 className="text-2xl font-semibold mb-1">{labels.pageTitle}</h1>
      <p className="text-eoe-text-sub dark:text-eoe-dark-text-sub mb-6">{labels.pageSubtitle}</p>

      {items.length === 0 ? (
        <p className="text-eoe-text-sub dark:text-eoe-dark-text-sub">{labels.empty}</p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => {
            const commentId = getCommentId(item.comment);
            const actionId = getActionPostId(item.comment);
            const title = getTaskBody(item.comment);
            const due = item.due_at
              ? new Date(item.due_at).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })
              : labels.noDue;
            return (
              <li
                key={`${String(item.assignment_rel_id)}-${String(commentId ?? "")}`}
                className="border border-eoe-border-main dark:border-eoe-dark-border-main rounded-lg p-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-medium text-eoe-text dark:text-eoe-dark-text">{title}</p>
                  <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {labels.dueLabel}: {due}
                  </p>
                  <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub mt-1">
                    {item.completed ? labels.completed : labels.incomplete}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {actionId != null ? (
                    <Link
                      to={`/action/${actionId}`}
                      className="btn-secondary text-sm py-2 px-3"
                    >
                      {labels.openAction}
                    </Link>
                  ) : null}
                  {commentId != null ? (
                    <button
                      type="button"
                      className="btn-primary text-sm py-2 px-3"
                      disabled={completionMutation.isPending}
                      onClick={() => onToggleComplete(item)}
                      aria-pressed={item.completed}
                    >
                      {item.completed ? labels.markIncomplete : labels.markComplete}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default UserTasksPage;
